/**
 * ================================================================
 * API SERVICE: ModificationService
 * ================================================================
 *
 * @service          modificationApi
 * @description      API service for client modification workflows (contact, bank, income, segments, nominee, account closure)
 * @baseUrl          modification base segment (createBaseQuery('modification'))
 *
 * @endpoints
 * - POST /v2/modification/profile-modifications        - Mobile/Email modification (OTP flow)
 * - POST /v2/modification/bank-addition                - Bank addition multi-step
 * - POST /v2/modification/default-bank-change          - Change default bank
 * - POST /v2/modification/income-modification          - Income proof modification
 * - POST /v2/modification/segment-addition             - Segment addition
 * - POST /v2/modification/nominee-addition             - Nominee operations
 * - GET  /v2/modification/close-account-details        - Fetch account closure meta
 * - POST /v2/modification/acc-closing-esign-proccess   - Account closure e-sign
 * - GET  /v1/user/nom-pan-verify (panDetailsApi)       - PAN verification
 *
 * @authentication  AuthToken header via createBaseQuery
 * @caching         Mutations no cache; selective queries minimal cache
 * @errorHandling   Standardized createApiErrorTransformer per endpoint tag
 * ================================================================
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { commonParams, createBaseQuery } from '@/lib/api/baseConfig';
import { createApiErrorTransformer, handleApiSuccess } from '@/lib/api/baseQuery';
import type { APIResponse } from '@/types/api';
import type {
  AccountClosureParams,
  AccountClosureWithTransferParams,
  AddBankDetailsFormValues,
  BankAdditionResponse,
  ChangeDefaultBankParams,
  CloseAccountDetailsQuery,
  IncomeModificationParams,
  IncomeResponse,
  MobileEmailResponse,
  MobileEmailSendRequest,
  ModifyMobileEmailRequest,
  NomineeParams,
  PanDetailsData,
  PanDetailsQuery,
  SegmentAdditionParams,
} from '@/types/kyc/modification';

export const modificationApi = createApi({
  reducerPath: 'modificationApi',
  baseQuery: createBaseQuery('modification'),
  endpoints: (builder) => ({
    // Mobile/Email modification
    modifyMobileEmail: builder.mutation<MobileEmailResponse, ModifyMobileEmailRequest>({
      query: ({ type, process, body, clientCode }) => {
        // For send_otp and resend_otp, only send input
        // For verify_otp, send both input and otp
        const requestBody =
          process === 'verify_otp' ? body : { input: (body as MobileEmailSendRequest).input };

        return {
          url: `/v2/modification/profile-modifications`,
          method: 'POST',
          body: requestBody,
          params: { ...commonParams, process, types: type, clientCode },
          // responseHandler: process === "verify_otp" ? "text" : "json",
        };
      },
      transformResponse: (response: MobileEmailResponse) => response,
      transformErrorResponse: createApiErrorTransformer('ModifyMobileEmail', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          handleApiSuccess('ModifyMobileEmail', data?.message || 'OTP step completed.');
        } catch {
          /* handled globally */
        }
      },
    }),

    // Bank operations
    addBankDetails: builder.mutation<BankAdditionResponse, AddBankDetailsFormValues>({
      query: ({ process, body, clientCode }) => ({
        url: `/v2/modification/bank-addition`,
        method: 'POST',
        body,
        params: { ...commonParams, process, clientCode },
      }),
      transformResponse: (response: BankAdditionResponse) => response,
      transformErrorResponse: createApiErrorTransformer('AddBankDetails', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          handleApiSuccess('AddBankDetails', data?.message || 'Bank details submitted.');
        } catch {}
      },
    }),

    changeDefaultBank: builder.mutation<APIResponse, ChangeDefaultBankParams>({
      query: ({ body, clientCode }) => ({
        url: `/v2/modification/default-bank-change`,
        method: 'POST',
        body,
        params: { ...commonParams, clientCode },
      }),
      transformResponse: (response: any) => response,
      transformErrorResponse: createApiErrorTransformer('ChangeDefaultBank', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          handleApiSuccess('ChangeDefaultBank', data?.message || 'Default bank changed.');
        } catch {}
      },
    }),

    // Income modification
    modifyIncome: builder.mutation<IncomeResponse, IncomeModificationParams>({
      query: ({ process, body, clientCode }) => {
        // Handle FormData for file uploads
        return {
          url: `/v2/modification/income-modification`,
          method: 'POST',
          body: body,
          params: { ...commonParams, process, clientCode },
        };
      },
      transformResponse: (response: IncomeResponse) => response,
      transformErrorResponse: createApiErrorTransformer('ModifyIncome', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          handleApiSuccess('ModifyIncome', data?.message || 'Income proof uploaded.');
        } catch {}
      },
    }),

    // Segment addition
    submitNewSegment: builder.mutation<any, SegmentAdditionParams>({
      query: ({ body, clientCode }) => ({
        url: `/v2/modification/segment-addition`,
        method: 'POST',
        body,
        params: { ...commonParams, process: 'verify_otp', clientCode },
      }),
      transformResponse: (response: any) => response,
      transformErrorResponse: createApiErrorTransformer('SubmitNewSegment', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('SubmitNewSegment', 'Segment addition e-sign initiated.');
        } catch {}
      },
    }),

    // TODO: Move to separate API service
    // getPanDetails: builder.query<any, any>({
    //   query: (query) => ({
    //     url: `/v1/user/nom-pan-verify`,
    //     method: 'GET',
    //     params: { ...commonParams, pan_no: query.pan },
    //   }),
    //   transformResponse: (response: any) => {
    //     return response;
    //   },
    //   transformErrorResponse: (response: any) => {
    //     if (import.meta.env.NODE_ENV === 'development') {
    //       console.error('❌ PAN Details API Error:', response);
    //     }
    //     return response;
    //   },
    // }),

    // Nominee operations
    handleNominee: builder.mutation<any, NomineeParams>({
      query: ({ body, clientCode }) => ({
        url: `/v2/modification/nominee-addition`,
        method: 'POST',
        body,
        params: { ...commonParams, clientCode },
      }),
      transformResponse: (response: any) => response,
      transformErrorResponse: createApiErrorTransformer('HandleNominee', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('HandleNominee', 'Add Nominee e-sign initiated.');
        } catch {}
      },
    }),

    closeAccountDetails: builder.query<any, CloseAccountDetailsQuery>({
      query: ({ clientCode }) => ({
        url: `/v2/modification/close-account-details`,
        method: 'GET',
        params: { ...commonParams, clientCode },
      }),
      transformResponse: (response: any) => response,
      transformErrorResponse: createApiErrorTransformer('CloseAccountDetails', true),
    }),

    // Account closure
    accountClosure: builder.mutation<any, AccountClosureParams>({
      query: ({ body, clientCode }) => ({
        url: `/v2/modification/acc-closing-esign-proccess`,
        method: 'POST',
        body,
        params: { ...commonParams, clientCode },
      }),
      transformResponse: (response: any) => response,
      transformErrorResponse: createApiErrorTransformer('AccountClosure', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('AccountClosure', 'Account closure e-sign initiated.');
        } catch {}
      },
    }),

    accountClosureWithTransfer: builder.mutation<any, AccountClosureWithTransferParams>({
      query: ({ body, clientCode, closeIn }) => ({
        url: `/v2/modification/acc-closing-with-transfer-esign-proccess`,
        method: 'POST',
        body,
        params: { ...commonParams, clientCode, close_in: closeIn },
      }),
      transformResponse: (response: any) => response,
      transformErrorResponse: createApiErrorTransformer('AccountClosureWithTransfer', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess(
            'AccountClosureWithTransfer',
            'Account closure with transfer e-sign initiated.',
          );
        } catch {}
      },
    }),
  }),
});

export const {
  useModifyMobileEmailMutation,
  useAddBankDetailsMutation,
  useChangeDefaultBankMutation,
  useModifyIncomeMutation,
  useSubmitNewSegmentMutation,
  useHandleNomineeMutation,
  useCloseAccountDetailsQuery,
  useAccountClosureMutation,
  useAccountClosureWithTransferMutation,
} = modificationApi;

// Separate API for PAN Details (uses different base URL)
export const panDetailsApi = createApi({
  reducerPath: 'panDetailsApi',
  baseQuery: createBaseQuery('pan'),
  endpoints: (builder) => ({
    getPanDetails: builder.query<APIResponse<PanDetailsData>, PanDetailsQuery>({
      query: ({ pan }) => ({
        url: `/v2/external/pan-verification`,
        method: 'POST',
        body: { panNo: pan },
        params: { ...commonParams },
      }),
    }),
  }),
});

export const { useGetPanDetailsQuery, useLazyGetPanDetailsQuery } = panDetailsApi;

/**
 * ================================================================
 * END OF SERVICE: ModificationService
 * ================================================================
 *
 * @exports          modificationApi, panDetailsApi and related hooks
 * @dependencies     RTK Query, baseQuery helpers, modification types
 * @usedBy           Modification forms & flows (profile, bank, income, segments)
 *
 * @notes
 * - Standardized error handling via createApiErrorTransformer per endpoint
 * - Future: Extract PAN details API maybe to dedicated compliance service
 * ================================================================
 */
